/**
 * COMPROVANTES — foto ou PDF. Guardados no aparelho e, desde a 2G, enviados
 * ao R2 (o armazenamento de arquivos da Cloudflare) pelo mesmo «Sincronizar».
 *
 * NO APARELHO: arquivo não cabe no localStorage (o limite é de poucos
 * megabytes para tudo), então ele mora no IndexedDB, o armazém de arquivos do
 * navegador. Os registros (quitação, despesa, acerto, parcela antiga da ficha)
 * guardam só o id — o mesmo id que vai ao banco e ao R2.
 *
 * AO ANEXAR: foto é comprimida aqui mesmo (JPEG, lado maior 1600 px, ~200 KB).
 * No curral o sinal é fraco e o plano de dados é pouco; uma foto de 4 MB do
 * celular não sobe, e o comprovante continua legível com 200 KB. PDF vai como
 * está, até 8 MB; acima disso é recusado na hora, com a frase dizendo o porquê.
 *
 * AO SINCRONIZAR (no fim, depois de tudo): sobe cada arquivo cujo registro já
 * está no banco — o banco só aceita arquivo pendurado em registro que existe.
 * Registro ainda na fila: o arquivo espera o próximo «Sincronizar».
 *
 * SEM O R2 LIGADO: o servidor responde 503. O arquivo fica aqui, como antes,
 * e o contador mostra que ele está esperando. Não é recusa e não trava nada:
 * as fichas e o extrato sobem normalmente, e a versão nova do app continua
 * sendo oferecida (o arquivo no IndexedDB não depende da versão do app).
 *
 * ABRIR: primeiro do aparelho; se não estiver aqui (anexado em outro
 * aparelho), busca do servidor — que pergunta ao banco se a pessoa pode ver.
 * O que vem do servidor NÃO é guardado aqui: o IndexedDB é do aparelho, não
 * da pessoa, e o próximo a entrar neste celular não pode abrir pelo atalho.
 */
import { useSyncExternalStore } from 'react'
import { lerDono, lerEstado } from './armazem'
import { lerExtrato } from './extrato'
import { lerNotas } from './notas'
import { lerPagamentos } from './pagamentos'
import { api, Recusa, SemSinal } from './sessao'
import type { Comprovante } from '../dominio/tipos'

const BANCO = 'pesagem-arquivos'
const LOJA = 'comprovantes'

function abrir(): Promise<IDBDatabase> {
  return new Promise((ok, erro) => {
    const req = indexedDB.open(BANCO, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(LOJA)
    req.onsuccess = () => ok(req.result)
    req.onerror = () => erro(req.error)
  })
}

function pedir<T>(modo: IDBTransactionMode, fn: (l: IDBObjectStore) => IDBRequest): Promise<T> {
  return abrir().then((db) => new Promise<T>((ok, erro) => {
    const req = fn(db.transaction(LOJA, modo).objectStore(LOJA))
    req.onsuccess = () => ok(req.result as T)
    req.onerror = () => erro(req.error)
  }))
}

/* ---------- preparar: comprimir a foto, conferir o PDF ---------- */

const LADO_MAXIMO = 1600
const ALVO = 220 * 1024
/** o mesmo teto do servidor (servidor/arquivos.ts) e do banco (007) */
export const LIMITE_DE_ARQUIVO = 8 * 1024 * 1024
const ACEITOS = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'application/xml']
/** D4: o XML da nota fiscal chega com nomes diferentes conforme o celular */
const TIPOS_DE_XML = ['application/xml', 'text/xml']

/** Arquivo que não serve (PDF grande, formato estranho): a mensagem é a frase para a tela. */
export class ArquivoRecusado extends Error {}

function mb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
}

/** A foto pronta para guardar e subir; o PDF conferido. Lança `ArquivoRecusado` com a frase. */
export async function prepararArquivo(b: Blob): Promise<Blob> {
  const tipo = (b.type || '').toLowerCase()
  if (TIPOS_DE_XML.includes(tipo)) {
    if (b.size > LIMITE_DE_ARQUIVO) throw new ArquivoRecusado(`Este XML tem ${mb(b.size)} e o limite é 8 MB. Confira se é o XML da nota.`)
    // sempre com o mesmo nome de tipo: é o que o servidor e o banco aceitam
    return tipo === 'application/xml' ? b : new Blob([b], { type: 'application/xml' })
  }
  if (tipo === 'application/pdf') {
    if (b.size > LIMITE_DE_ARQUIVO) {
      throw new ArquivoRecusado(`Este PDF tem ${mb(b.size)} e o limite é 8 MB. Tire uma foto do comprovante ou mande um PDF menor.`)
    }
    return b
  }
  if (tipo.startsWith('image/') || !tipo) {
    // já pequena e num formato aceito: não mexe (recomprimir só piora)
    if (b.size <= ALVO && ACEITOS.includes(tipo)) return b
    try {
      return await comprimir(b)
    } catch {
      if (ACEITOS.includes(tipo) && b.size <= LIMITE_DE_ARQUIVO) return b
      throw new ArquivoRecusado('Não deu para ler esta foto. Tire a foto de novo ou mande o comprovante em PDF.')
    }
  }
  throw new ArquivoRecusado('Só dá para anexar foto ou PDF.')
}

async function desenhavel(b: Blob): Promise<{ img: CanvasImageSource; w: number; h: number; fechar: () => void }> {
  if (typeof createImageBitmap === 'function') {
    // createImageBitmap já gira a foto conforme o celular a tirou (EXIF)
    const bmp = await createImageBitmap(b)
    return { img: bmp, w: bmp.width, h: bmp.height, fechar: () => bmp.close() }
  }
  const url = URL.createObjectURL(b)
  const img = new Image()
  await new Promise<void>((ok, erro) => { img.onload = () => ok(); img.onerror = () => erro(new Error('foto')); img.src = url })
  return { img, w: img.naturalWidth, h: img.naturalHeight, fechar: () => URL.revokeObjectURL(url) }
}

async function comprimir(b: Blob): Promise<Blob> {
  const d = await desenhavel(b)
  try {
    let menor: Blob | null = null
    // diminui primeiro a qualidade, depois o tamanho, até caber em ~200 KB
    for (const lado of [LADO_MAXIMO, 1280, 1024]) {
      const escala = Math.min(1, lado / Math.max(d.w, d.h))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(d.w * escala))
      canvas.height = Math.max(1, Math.round(d.h * escala))
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('sem canvas')
      ctx.fillStyle = '#fff' // PNG com fundo transparente vira fundo branco, não preto
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(d.img, 0, 0, canvas.width, canvas.height)
      for (const q of [0.8, 0.7, 0.6, 0.5]) {
        const saida = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', q))
        if (!saida) throw new Error('sem jpeg')
        if (!menor || saida.size < menor.size) menor = saida
        if (saida.size <= ALVO) return saida
      }
    }
    if (!menor) throw new Error('sem jpeg')
    return menor
  } finally {
    d.fechar()
  }
}

/* ---------- guardar e ler no aparelho ---------- */

/**
 * Guarda no aparelho JÁ PREPARADO (foto comprimida). Devolve o que foi
 * guardado — o tamanho e o tipo de verdade. Lança `ArquivoRecusado` com a
 * frase para a tela quando o arquivo não serve.
 */
export async function guardarArquivo(id: string, arquivo: Blob): Promise<Blob> {
  const pronto = await prepararArquivo(arquivo)
  await pedir('readwrite', (l) => l.put(pronto, id))
  avisarMudanca()
  return pronto
}

export function lerArquivo(id: string): Promise<Blob | undefined> {
  return pedir('readonly', (l) => l.get(id))
}

export function apagarArquivo(id: string): Promise<unknown> {
  return pedir('readwrite', (l) => l.delete(id))
}

async function idsNoAparelho(): Promise<Set<string>> {
  try {
    return new Set((await pedir<IDBValidKey[]>('readonly', (l) => l.getAllKeys())).map(String))
  } catch {
    return new Set()
  }
}

/* ---------- abrir (do aparelho ou do servidor) ---------- */

/**
 * Abre o comprovante numa aba nova (foto ou PDF). Devolve `null` se abriu, ou
 * a frase para a tela. Chamar DIRETO no toque: a aba é aberta antes de
 * qualquer espera, senão o navegador a bloqueia como janela intrometida.
 */
export async function abrirComprovante(id: string): Promise<string | null> {
  const janela = window.open('', '_blank')
  const mostrar = (b: Blob) => {
    // D4: o XML da nota abre como TEXTO. Como página, um XML com programa
    // dentro rodaria com o endereço do app.
    const url = URL.createObjectURL(TIPOS_DE_XML.includes(b.type) ? new Blob([b], { type: 'text/plain;charset=utf-8' }) : b)
    if (janela) janela.location.href = url
    else window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
  const desistir = (frase: string) => { janela?.close(); return frase }
  try {
    const local = await lerArquivo(id).catch(() => undefined)
    if (local) { mostrar(local); return null }
    const r = await buscarDoServidor(id)
    if (typeof r === 'string') return desistir(r)
    mostrar(r)
    return null
  } catch {
    return desistir('Não deu para abrir o arquivo.')
  }
}

async function buscarDoServidor(id: string): Promise<Blob | string> {
  let r: Response
  try {
    r = await fetch(`/api/arquivos/${encodeURIComponent(id)}`, { credentials: 'same-origin', cache: 'no-store' })
  } catch {
    return 'Sem internet: este arquivo não está neste aparelho. Abra de novo quando tiver sinal.'
  }
  const tipo = (r.headers.get('content-type') ?? '').split(';')[0]!.trim()
  if (r.ok && ACEITOS.includes(tipo)) return await r.blob()
  if (r.ok) return 'Sem internet: este arquivo não está neste aparelho. Abra de novo quando tiver sinal.' // portal de wi-fi
  let dados: Record<string, unknown> = {}
  try { dados = await r.json() } catch { /* sem corpo: fica a frase genérica */ }
  if (r.status === 401) return 'Entre de novo no app para abrir este arquivo.'
  if (dados['codigo'] === 'sem_armazenamento') {
    return 'O armazenamento de arquivos ainda não foi ligado. O arquivo está só no aparelho de quem anexou.'
  }
  return String(dados['erro'] ?? 'Não deu para abrir o arquivo.')
}

/* ---------- o que já subiu e o que veio do servidor (por pessoa) ---------- */

/** Um arquivo que o servidor diz que a pessoa pode abrir. */
export interface ArquivoDoServidor {
  id: string
  de_tipo: string
  de_id: string
  nome: string
  tipo: string
  tamanho: number
  anulado_em: string | null
}

interface EstadoDosArquivos {
  /** o que o servidor tem e esta pessoa pode abrir — inclusive o que subiu daqui */
  doServidor: Record<string, ArquivoDoServidor>
  /** recusados de vez (ex.: PDF acima do limite, anexado antes da 2G): ficam no aparelho, fora do contador */
  recusados: Record<string, string>
  /** o servidor disse, na última vez, se o R2 está ligado */
  armazenamento: boolean | null
  ultimaSync: string | null
}

const PREFIXO = 'pesagem.arquivos.v1.'

function vazio(): EstadoDosArquivos {
  return { doServidor: {}, recusados: {}, armazenamento: null, ultimaSync: null }
}

let donoAberto: string | null = null
let estado = vazio()

function garantirDono(): void {
  const id = lerDono()?.id ?? null
  if (id === donoAberto) return
  donoAberto = id
  try {
    const t = id ? localStorage.getItem(PREFIXO + id) : null
    const e = t ? (JSON.parse(t) as Partial<EstadoDosArquivos>) : null
    estado = {
      doServidor: e?.doServidor && typeof e.doServidor === 'object' ? e.doServidor : {},
      recusados: e?.recusados && typeof e.recusados === 'object' ? e.recusados : {},
      armazenamento: typeof e?.armazenamento === 'boolean' ? e.armazenamento : null,
      ultimaSync: e?.ultimaSync ?? null,
    }
  } catch {
    estado = vazio()
  }
  contagem = { ...contagem, pessoa: id }
}

function mudar(fn: (e: EstadoDosArquivos) => void): void {
  garantirDono()
  const novo = structuredClone(estado)
  fn(novo)
  estado = novo
  try { if (donoAberto) localStorage.setItem(PREFIXO + donoAberto, JSON.stringify(estado)) } catch { /* sem disco */ }
  avisarMudanca()
}

/* ---------- a quem cada arquivo pertence ---------- */

interface Referencia {
  id: string
  deTipo: 'ficha' | 'quitacao' | 'despesa' | 'acerto' | 'nota' | 'recebimento' | 'recebimento_comissao' | 'pagamento_comissao'
  deId: string
  nome: string
  /** o registro já está no banco: o arquivo pode subir */
  pronto: boolean
}

/**
 * Todo arquivo que algum registro DESTE aparelho cita, e se o registro já
 * subiu. Registro recusado pelo servidor não entra: nunca vai existir no banco.
 */
function referencias(): Referencia[] {
  const lista: Referencia[] = []
  const pg = lerPagamentos()
  for (const q of pg.quitacoes) {
    if (q.recusada) continue
    const pronto = !pg.pendentes.quitacoes.includes(q.id)
    for (const c of q.comprovantes ?? []) lista.push({ id: c.id, deTipo: 'quitacao', deId: q.id, nome: c.nome, pronto })
  }
  // 3F: o comprovante do recebimento da revenda — sobe depois do recebimento
  for (const x of pg.recebimentos) {
    if (x.recusado) continue
    const pronto = !pg.pendentes.recebimentos.includes(x.id)
    for (const c of x.comprovantes ?? []) lista.push({ id: c.id, deTipo: 'recebimento', deId: x.id, nome: c.nome, pronto })
  }
  // R2B (014): o comprovante do recebimento da comissão do cliente e o do pagamento da comissão
  for (const x of pg.recebimentosComissao) {
    if (x.recusado) continue
    const pronto = !pg.pendentes.recebimentosComissao.includes(x.id)
    for (const c of x.comprovantes ?? []) lista.push({ id: c.id, deTipo: 'recebimento_comissao', deId: x.id, nome: c.nome, pronto })
  }
  for (const x of pg.pagamentosComissao) {
    if (x.recusado) continue
    const pronto = !pg.pendentes.pagamentosComissao.includes(x.id)
    for (const c of x.comprovantes ?? []) lista.push({ id: c.id, deTipo: 'pagamento_comissao', deId: x.id, nome: c.nome, pronto })
  }
  const ext = lerExtrato()
  for (const l of ext.lancamentos) {
    if (!l.comprovanteId || l.recusa) continue
    lista.push({ id: l.comprovanteId, deTipo: 'despesa', deId: l.id, nome: l.comprovante?.nome ?? 'comprovante da despesa',
      pronto: !ext.pendentes.includes(l.id) })
  }
  // acerto só existe aqui depois de feito no servidor
  for (const a of ext.acertos) {
    if (a.comprovanteId) lista.push({ id: a.comprovanteId, deTipo: 'acerto', deId: a.id, nome: 'comprovante do acerto', pronto: true })
    // R1A: o comprovante do pagamento registrado depois (gestor ou Consulta)
    if (a.pagoComprovanteId) lista.push({ id: a.pagoComprovanteId, deTipo: 'acerto', deId: a.id, nome: 'comprovante do pagamento do acerto', pronto: true })
  }
  // D4: o XML, o PDF ou a foto da nota fiscal — sobe depois da nota
  const nt = lerNotas()
  for (const n of nt.notas) {
    if (!n.arquivoId || n.recusa) continue
    lista.push({ id: n.arquivoId, deTipo: 'nota', deId: n.id, nome: n.arquivo?.nome ?? 'arquivo da nota', pronto: !nt.pendentes.includes(n.id) })
  }
  // etapa 1: comprovante anotado na própria parcela, dentro da ficha
  const est = lerEstado()
  for (const f of est.fichas) {
    // R1A · contador preso: ficha recusada pelo servidor nunca chega ao banco,
    // e o arquivo dela contaria «para enviar» para sempre
    if (f.id in est.recusadas.fichas && !est.pendentes.fichas.includes(f.id)) continue
    const pronto = f.revServidor !== undefined
    for (const p of f.pagamentos ?? []) {
      for (const c of p.comprovantes ?? []) lista.push({ id: c.id, deTipo: 'ficha', deId: f.id, nome: c.nome, pronto })
    }
    // R2B: o comprovante do frete e do gasto mora na ficha (é dela, como o gasto)
    for (const g of f.custos ?? []) {
      for (const c of g.comprovantes ?? []) lista.push({ id: c.id, deTipo: 'ficha', deId: f.id, nome: c.nome, pronto })
    }
  }
  // R2B (revisão de 06/10/2026): a Consulta só dá baixa — o arquivo da ficha, da
  // despesa e da nota de outra pessoa não é dela para enviar (o servidor recusaria,
  // e o erro de um arquivo alheio aparecia na tela dela). Fica de fora da fila dela.
  if (lerDono()?.papel === 'consulta') {
    const dela = new Set(['quitacao', 'recebimento', 'acerto', 'recebimento_comissao', 'pagamento_comissao'])
    return lista.filter((x) => dela.has(x.deTipo))
  }
  return lista
}

/** O que está neste aparelho e ainda não subiu (nem foi recusado de vez). */
async function aEnviar(): Promise<Referencia[]> {
  garantirDono()
  if (!donoAberto) return []
  const aqui = await idsNoAparelho()
  const vistos = new Set<string>()
  return referencias().filter((r) => {
    if (vistos.has(r.id)) return false
    vistos.add(r.id)
    return aqui.has(r.id) && !estado.doServidor[r.id] && !estado.recusados[r.id]
  })
}

/* ---------- o contador do «Sincronizar» ---------- */

export interface ContagemDeArquivos {
  /** arquivos deste aparelho que ainda não subiram */
  pendentes: number
  /** o servidor disse que o R2 ainda não está ligado: eles estão esperando, não recusados */
  semArmazenamento: boolean
  pessoa: string | null
}

let contagem: ContagemDeArquivos = { pendentes: 0, semArmazenamento: false, pessoa: null }
const ouvintes = new Set<() => void>()
let recontando: Promise<void> | null = null
let recontarDeNovo = false

function avisarMudanca(): void {
  ouvintes.forEach((f) => f())
}

/**
 * Reconta os arquivos a enviar. Assíncrono (o IndexedDB não responde na
 * hora): a tela chama sempre que algo muda, e o número aparece em seguida.
 */
export function recontarArquivos(): Promise<void> {
  if (recontando) { recontarDeNovo = true; return recontando }
  recontando = (async () => {
    try {
      do {
        recontarDeNovo = false
        const n = (await aEnviar()).length
        const nova = { pendentes: n, semArmazenamento: estado.armazenamento === false && n > 0, pessoa: donoAberto }
        if (nova.pendentes !== contagem.pendentes || nova.semArmazenamento !== contagem.semArmazenamento || nova.pessoa !== contagem.pessoa) {
          contagem = nova
          avisarMudanca()
        }
      } while (recontarDeNovo)
    } catch {
      /* sem IndexedDB: o contador fica como estava */
    } finally {
      recontando = null
    }
  })()
  return recontando
}

export function usarArquivosPendentes(): ContagemDeArquivos {
  return useSyncExternalStore(
    (f) => { ouvintes.add(f); return () => ouvintes.delete(f) },
    () => contagem,
  )
}

/** Os comprovantes de um registro: os anexados aqui mais os que o servidor diz que existem. */
export function usarComprovantesDoServidor(): Record<string, ArquivoDoServidor> {
  return useSyncExternalStore(
    (f) => { ouvintes.add(f); return () => ouvintes.delete(f) },
    () => { garantirDono(); return estado.doServidor },
  )
}

export function comprovantesDe(doServidor: Record<string, ArquivoDoServidor>, deTipo: string, deId: string, locais: Comprovante[] = []): Comprovante[] {
  const ids = new Set(locais.map((c) => c.id))
  const deLa = Object.values(doServidor)
    .filter((a) => a.de_tipo === deTipo && a.de_id === deId && !ids.has(a.id) && !a.anulado_em)
    .map((a) => ({ id: a.id, nome: a.nome, tipo: a.tipo, tamanho: a.tamanho }))
  return [...locais, ...deLa]
}

/* ---------- sincronizar (chamado no fim do «Sincronizar», em fila.ts) ---------- */

interface Placar { subiram: number; desceram: number; recusas: string[] }

/**
 * Sobe os arquivos cujo registro já está no banco e desce a lista do que a
 * pessoa pode abrir. Sem sinal: lança `SemSinal` (para tudo, nada se perde).
 * Sem o R2: para de tentar subir, sem recusa; a lista desce assim mesmo.
 */
export async function sincronizarArquivos(r: Placar): Promise<void> {
  if (!lerDono()) return
  try {
    await subir(r)
    await descer(r)
  } catch (e) {
    if (e instanceof Recusa) { r.recusas.push(`Arquivos: ${e.message}`); return }
    throw e
  } finally {
    await recontarArquivos()
  }
}

async function subir(r: Placar): Promise<void> {
  for (const ref of await aEnviar()) {
    if (!ref.pronto) continue // o registro dele ainda está na fila: vai no próximo
    const blob = await lerArquivo(ref.id).catch(() => undefined)
    if (!blob) continue
    let pronto: Blob
    try {
      // arquivo anexado antes da 2G foi guardado sem comprimir
      pronto = await prepararArquivo(blob)
    } catch (e) {
      const frase = e instanceof ArquivoRecusado ? e.message : 'Arquivo ilegível.'
      mudar((x) => { x.recusados[ref.id] = frase })
      r.recusas.push(`Arquivo «${ref.nome}» não subiu: ${frase} Ele continua neste aparelho.`)
      continue
    }
    const q = new URLSearchParams({ id: ref.id, de_tipo: ref.deTipo, de_id: ref.deId, nome: ref.nome })
    let resp: Response
    try {
      resp = await fetch(`/api/arquivos?${q}`, {
        method: 'POST', body: pronto, credentials: 'same-origin',
        headers: { 'content-type': pronto.type || 'application/octet-stream' },
      })
    } catch {
      throw new SemSinal('Sem conexão com a internet.')
    }
    if (!(resp.headers.get('content-type') ?? '').includes('application/json')) throw new SemSinal('Servidor fora do ar.')
    let dados: Record<string, unknown> = {}
    try { dados = await resp.json() } catch { throw new SemSinal('Resposta incompleta do servidor.') }

    if (resp.ok) {
      const a = (dados['arquivo'] ?? {}) as Partial<ArquivoDoServidor>
      mudar((x) => {
        x.armazenamento = true
        x.doServidor[ref.id] = {
          id: ref.id, de_tipo: ref.deTipo, de_id: ref.deId, nome: a.nome ?? ref.nome,
          tipo: a.tipo ?? pronto.type, tamanho: Number(a.tamanho ?? pronto.size), anulado_em: null,
        }
      })
      r.subiram++
      continue
    }
    if (resp.status === 503 && dados['codigo'] === 'sem_armazenamento') {
      // o R2 ainda não foi ligado: todos esperam, sem recusa
      mudar((x) => { x.armazenamento = false })
      return
    }
    if (resp.status === 409 && dados['erro'] === 'dono_ausente') continue // espera o registro chegar
    const frase = String(dados['erro'] ?? 'não deu para concluir.')
    if (resp.status === 401) throw new Recusa(frase, 401, dados)
    if (resp.status >= 400 && resp.status < 500) {
      // recusa de vez: mandar de novo daria a mesma resposta para sempre e o
      // contador nunca zeraria. Sai da conta e fica aqui — nada se apaga.
      mudar((x) => { x.recusados[ref.id] = frase })
      r.recusas.push(`Arquivo «${ref.nome}» não subiu: ${frase} Ele continua neste aparelho.`)
      continue
    }
    r.recusas.push(`Arquivo «${ref.nome}»: ${frase}`) // 5xx: tenta de novo no próximo
  }
}

async function descer(r: Placar): Promise<void> {
  garantirDono()
  const desde = estado.ultimaSync
  const d = await api<{ agora: string; armazenamento: boolean; arquivos: (ArquivoDoServidor & { anulado_em: string | null })[] }>(
    `arquivos${desde ? `?desde=${encodeURIComponent(desde)}` : ''}`)
  mudar((x) => {
    for (const a of d.arquivos ?? []) {
      if (!x.doServidor[a.id]) r.desceram++
      x.doServidor[a.id] = {
        id: a.id, de_tipo: a.de_tipo, de_id: a.de_id, nome: a.nome, tipo: a.tipo, tamanho: Number(a.tamanho),
        anulado_em: a.anulado_em ? String(a.anulado_em) : null,
      }
    }
    // a lista diz se o R2 está ligado agora (é o servidor que sabe)
    if (typeof d.armazenamento === 'boolean') x.armazenamento = d.armazenamento
    x.ultimaSync = d.agora || x.ultimaSync
  })
}

/**
 * R1A · «Sair»: as fotos e PDFs que JÁ ESTÃO no servidor saem deste aparelho
 * (abrem de novo pelo servidor, com sinal); os que ainda não subiram ficam —
 * nada se perde. A lista do que a pessoa pode abrir também sai.
 */
export async function enxugarArquivosAoSair(): Promise<void> {
  garantirDono()
  if (!donoAberto) return
  for (const id of Object.keys(estado.doServidor)) {
    try { await apagarArquivo(id) } catch { /* sem IndexedDB: fica como está */ }
  }
  mudar((e) => { e.doServidor = {}; e.ultimaSync = null })
}

/* ---------- miudezas ---------- */

export function baixar(conteudo: BlobPart, nome: string, tipo: string): void {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }))
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
